import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Divider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import BlurImage from 'components/BlurImage';
import Skeleton from 'react-loading-skeleton';
import ModalLayout, { DEFAULT_MODAL_Z_INDEX } from '../ModalLayout';
import DetailPane from './DetailPane';
import AdditionalDetailsPane from './AdditionalDetailsPane';
import FeaturePane from './FeaturePane';
import ImageGalleryModal from '../ImageGallery';
import InquiryModal from '../InquiryModal';
import { useModals } from 'pages/MainProvider';
import usePropertiesStore from 'store/usePropertiesStore';
import useUserStore from 'store/useUserStore';
import { PropertyStatisticsActionEnum } from 'types/PropertyStatistics';
import PropertyActionButton from './PropertyActionButton';
import { PropertyDTO } from 'util/Types';
import { ModalLayoutProps } from '..';
import styles from './propertydetail.module.css';
import { usePropertyStatistics } from 'util/propertyStatisticsHelper';
import { PropertyAdditionalDetail } from 'types/Property';

// Helper function to validate a single image
const validateImage = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            // Check if image has reasonable dimensions (not too small)
            if (img.width >= 100 && img.height >= 100) {
                resolve(true);
            } else {
                resolve(false);
            }
        };
        img.onerror = () => resolve(false);
        img.src = url;
    });
};

// Custom hook to validate images
const useImageValidation = (imageUrls: string[] = []) => {
    const [validImages, setValidImages] = useState<string[]>([]);
    const [isValidating, setIsValidating] = useState(false);

    // Create a stable string representation of the array to use as dependency
    const imageUrlsString = useMemo(() => {
        if (!imageUrls || imageUrls.length === 0) return '';
        return imageUrls.join('|');
    }, [imageUrls]);

    useEffect(() => {
        if (!imageUrls || imageUrls.length === 0) {
            setValidImages([]);
            setIsValidating(false);
            return;
        }

        setIsValidating(true);

        const validateImages = async () => {
            try {
                const validationPromises = imageUrls.map(async (url) => {
                    const isValid = await validateImage(url);
                    return isValid ? url : null;
                });

                const results = await Promise.all(validationPromises);
                const valid = results.filter(Boolean) as string[];
                setValidImages(valid);
            } catch (error) {
                console.error('Error validating images:', error);
                setValidImages([]);
            } finally {
                setIsValidating(false);
            }
        };

        validateImages();
    }, [imageUrlsString]); // Use stable string dependency instead of array

    return { validImages, isValidating };
};

interface MainPropertyDetailModalProps extends ModalLayoutProps {
    property: PropertyDTO | null;
    zIndex?: number;
    isPublic?: boolean;
}

const MainPropertyDetailModal = (props: MainPropertyDetailModalProps) => {
    const { property, visible, onClose, zIndex = DEFAULT_MODAL_Z_INDEX, isPublic = false } = props;

    const { saveStatistics } = usePropertyStatistics();
    const { isCompetitorPreviewOpened } = useModals();
    const { userInfo } = useUserStore();
    const { fetchPropertyImages } = usePropertiesStore();
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);

    // Validate property images - use stable array reference
    const propertyImageUrls = useMemo(() => property?.imageUrls || [], [property?.imageUrls]);
    const { validImages: validPropertyImages, isValidating: isValidatingPropertyImages } = useImageValidation(propertyImageUrls);

    // Track view statistics
    useQuery({
        queryKey: ['savePropertyStatistics', property?.webPageId],
        queryFn: async () => {
            if (!property?.webPageId) return 0;
            return await saveStatistics({
                webPageId: property.webPageId,
                action: PropertyStatisticsActionEnum.VIEW,
            });
        },
        enabled: !!property?.webPageId,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
        staleTime: 0,
        retry: 1,
    });

    // Fetch additional property details for logged-in users only
    const { data: additionalDetails, isLoading: isAdditionalDetailsLoading } = useQuery({
        queryKey: ['propertyAdditionalDetails', property?.webPageId],
        queryFn: async () => {
            if (!property?.webPageId) return null;
            return await fetchPropertyImages(property.webPageId);
        },
        enabled: !!property?.webPageId && !!userInfo, // Only fetch for logged-in users
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 2,
    });

    // Validate additional images if available - use stable array reference
    const additionalImageUrls = useMemo(() => additionalDetails?.images || [], [additionalDetails?.images]);
    const { validImages: validAdditionalImages, isValidating: isValidatingAdditionalImages } = useImageValidation(additionalImageUrls);

    // Handle image URL construction
    const getImageUrl = (imagePath: string) => {
        if (!property?.webPageUrl) return imagePath;
        const originUrl = new URL(property.webPageUrl);
        return imagePath.startsWith('http') ? imagePath : `${originUrl.origin}/${imagePath}`;
    };

    // Get fallback image URL from hellodata bucket
    const getFallbackImageUrl = useCallback(() => {
        if (!property?.propertyId) return '';
        return `https://cdnhellodatastore.azureedge.net/webpage-screenshots/${property.propertyId}.png`;
    }, [property?.propertyId]);

    // Get all available valid images (property images + additional images + fallback if needed)
    const allValidImages = useMemo(() => {
        const allImages = [...validPropertyImages, ...validAdditionalImages];

        // If no valid images and we have a property, add fallback
        if (allImages.length === 0 && property?.webPageId) {
            allImages.push(getFallbackImageUrl());
        }

        return allImages;
    }, [validPropertyImages, validAdditionalImages, property?.webPageId, getFallbackImageUrl]);

    const hasValidImages = allValidImages.length > 0;
    const isLoadingImages = isValidatingPropertyImages || isValidatingAdditionalImages;

    const Content = () => {
        if (!property) return null;

        return (
            <div className="min-h-screen bg-gray-50">
                {/* Sticky Header */}
                <div className="sticky top-0 z-10 bg-white shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-start py-4 sm:items-center sm:min-h-[4rem]">
                            <div className="flex flex-col flex-1 min-w-0">
                                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 break-words leading-tight line-clamp-3">{property.title}</h1>
                            </div>
                            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0 ml-2">
                                <CloseRoundedIcon className="text-gray-500" />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto">
                    {/* Hero Section - Full Width Image */}
                    <div className="relative w-full h-[70vh] overflow-hidden">
                        {/* Main Image - Clickable */}
                        <div
                            className="absolute inset-0 cursor-pointer"
                            onClick={() => {
                                if (hasValidImages) {
                                    setIsGalleryOpen(true);
                                }
                            }}
                        >
                            <div className="w-full h-full">
                                {isLoadingImages ? (
                                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                        <div className="animate-pulse bg-gray-200 w-full h-full"></div>
                                    </div>
                                ) : validPropertyImages.length > 0 ? (
                                    <img src={validPropertyImages[0]} alt={property.title} className="w-full h-full object-cover" />
                                ) : property?.webPageId ? (
                                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                        <img
                                            src={getFallbackImageUrl()}
                                            className="w-full h-full object-cover"
                                            style={{ objectPosition: 'center 7%' }}
                                            alt={property.title}
                                        />
                                    </div>
                                ) : (
                                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                        <span className="text-gray-500 text-lg">No image available</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* View Images Button - Top Right Corner */}
                        {hasValidImages && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent triggering the main image click
                                    setIsGalleryOpen(true);
                                }}
                                className="absolute top-4 right-4 bg-black/70 hover:bg-black/80 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-medium transition-colors text-xs sm:text-sm shadow-lg backdrop-blur-sm z-5 min-w-[60px] min-h-[36px] flex items-center justify-center"
                            >
                                <span className="flex items-center gap-1 sm:gap-2">
                                    <span>📷</span>
                                    <span>{allValidImages.length}</span>
                                </span>
                            </button>
                        )}

                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none">
                            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
                                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                                    <div className="flex flex-col gap-4">
                                        <div className="flex-1">
                                            <p className="text-white/90 text-lg sm:text-xl mb-2">
                                                {property.address?.City}, {property.address?.Country}
                                            </p>
                                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                                                <h2 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white whitespace-nowrap">
                                                    {property.details?.price ? `€${property.details.price.toLocaleString()}` : 'Price on Request'}
                                                </h2>
                                                {property?.details?.availability && (
                                                    <div
                                                        className={`${
                                                            ['sold', 'rented'].includes(property.details.availability.toLowerCase())
                                                                ? 'bg-red-500/90'
                                                                : property.details.availability.toLowerCase() === 'reserved'
                                                                  ? 'bg-yellow-500/90'
                                                                  : 'bg-green-500/90'
                                                        } text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-xs sm:text-sm font-medium w-fit`}
                                                    >
                                                        {property.details.availability.charAt(0).toUpperCase() +
                                                            property.details.availability.slice(1).toLowerCase()}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content Grid */}
                    <div className="px-4 sm:px-6 lg:px-8 py-12 xl:pb-12 pb-24">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                            {/* Left Column - Main Content */}
                            <div className="lg:col-span-8 space-y-8">
                                {/* Quick Stats with Enhanced Design */}
                                <div className="bg-white rounded-2xl p-8 shadow-sm">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                        <div className="bg-gray-50 rounded-xl p-4">
                                            <div className="text-sm text-gray-500 mb-1">Property Type</div>
                                            <div className="text-lg font-medium text-gray-900">{property.details?.propertyType || 'Not specified'}</div>
                                        </div>
                                        <div className="bg-gray-50 rounded-xl p-4">
                                            <div className="text-sm text-gray-500 mb-1">Price per m²</div>
                                            <div className="text-lg font-medium text-gray-900">
                                                {property.details?.pricePerSquareMeter
                                                    ? `€ ${Math.round(property.details.pricePerSquareMeter).toLocaleString()}`
                                                    : 'N/A'}
                                            </div>
                                        </div>
                                        <div className="bg-gray-50 rounded-xl p-4">
                                            <div className="text-sm text-gray-500 mb-1">Listing Type</div>
                                            <div className="text-lg font-medium text-gray-900">
                                                {property.details.listingType === 'rental' ? 'Rental' : 'Sale'}
                                            </div>
                                        </div>
                                        <div className="bg-gray-50 rounded-xl p-4">
                                            <div className="text-sm text-gray-500 mb-1">Availability</div>
                                            <div className="text-lg font-medium text-gray-900">Available</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Property Features - Enhanced Section */}
                                <div className="bg-white rounded-2xl p-8 shadow-sm">
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-6">Property Features</h3>

                                    {/* Key Features Grid - List on small screens, grid on larger screens */}
                                    <div className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                                        {/* Basic Information */}
                                        {property.details?.bedrooms > 0 && (
                                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-blue-600 text-xl">🛏️</span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-base font-medium text-gray-900 break-words">Bedrooms</p>
                                                    <p className="text-sm text-gray-500 break-words">{property.details.bedrooms}</p>
                                                </div>
                                            </div>
                                        )}

                                        {property.details?.bathrooms > 0 && (
                                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-purple-600 text-xl">🚿</span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-base font-medium text-gray-900 break-words">Bathrooms</p>
                                                    <p className="text-sm text-gray-500 break-words">{property.details.bathrooms}</p>
                                                </div>
                                            </div>
                                        )}

                                        {property.details?.sizeLiving > 0 && (
                                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                <div className="w-12 h-12 rounded-full bg-yellow-50 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-yellow-600 text-xl">📏</span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-base font-medium text-gray-900 break-words">Living Space</p>
                                                    <p className="text-sm text-gray-500 break-words">{property.details.sizeLiving} m²</p>
                                                </div>
                                            </div>
                                        )}

                                        {property.details?.sizePlot > 0 && (
                                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-indigo-600 text-xl">🌳</span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-base font-medium text-gray-900 break-words">Plot Size</p>
                                                    <p className="text-sm text-gray-500 break-words">{property.details.sizePlot} m²</p>
                                                </div>
                                            </div>
                                        )}

                                        {property.details?.constructionYear && (
                                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-orange-600 text-xl">🏗️</span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-base font-medium text-gray-900 break-words">Construction Year</p>
                                                    <p className="text-sm text-gray-500 break-words">{property.details.constructionYear}</p>
                                                </div>
                                            </div>
                                        )}

                                        {property.details?.heatingType && (
                                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-red-600 text-xl">🔥</span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-base font-medium text-gray-900 break-words">Heating</p>
                                                    <p className="text-sm text-gray-500 break-words">{property.details.heatingType}</p>
                                                </div>
                                            </div>
                                        )}

                                        {property.details?.floorsCount > 0 && (
                                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-green-600 text-xl">🏢</span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-base font-medium text-gray-900 break-words">Total Floors</p>
                                                    <p className="text-sm text-gray-500 break-words">{property.details.floorsCount}</p>
                                                </div>
                                            </div>
                                        )}

                                        {property.details?.floorNumber > 0 && (
                                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-teal-600 text-xl">⬆️</span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-base font-medium text-gray-900 break-words">Floor Number</p>
                                                    <p className="text-sm text-gray-500 break-words">{property.details.floorNumber}</p>
                                                </div>
                                            </div>
                                        )}

                                        {property.details?.elevator && (
                                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                <div className="w-12 h-12 rounded-full bg-pink-50 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-pink-600 text-xl">🛗</span>
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-base font-medium text-gray-900 break-words">Elevator</p>
                                                    <p className="text-sm text-gray-500 break-words">Available</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Additional Features */}
                                    <div className="space-y-6">
                                        {/* Parking & Terrace */}
                                        {(property.details?.parkingTypes?.length > 0 || property.details?.terraceTypes?.length > 0) && (
                                            <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
                                                {property.details?.parkingTypes?.length > 0 && (
                                                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                        <div className="w-12 h-12 rounded-full bg-pink-50 flex items-center justify-center flex-shrink-0">
                                                            <span className="text-pink-600 text-xl">🚗</span>
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-base font-medium text-gray-900 break-words">Parking</p>
                                                            <p className="text-sm text-gray-500 break-words">{property.details.parkingTypes.join(', ')}</p>
                                                        </div>
                                                    </div>
                                                )}

                                                {property.details?.terraceTypes?.length > 0 && (
                                                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                        <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                                                            <span className="text-teal-600 text-xl">🌞</span>
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-base font-medium text-gray-900 break-words">Terrace</p>
                                                            <p className="text-sm text-gray-500 break-words">{property.details.terraceTypes.join(', ')}</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Energy & Renovation */}
                                        <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
                                            {property.details?.energyCertAvailable && (
                                                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                                                        <span className="text-blue-600 text-xl">⚡</span>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-base font-medium text-gray-900 break-words">Energy Certificate</p>
                                                        <p className="text-sm text-gray-500 break-words">{property.details.energyCertRating || 'Available'}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {property.details?.needsRenovation && (
                                                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                    <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                                                        <span className="text-orange-600 text-xl">🔨</span>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-base font-medium text-gray-900 break-words">Renovation Status</p>
                                                        <p className="text-sm text-gray-500 break-words">Needs Renovation</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Additional Features */}
                                        <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
                                            {property.details?.furnished && (
                                                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                    <div className="w-12 h-12 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0">
                                                        <span className="text-violet-600 text-xl">🪑</span>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-base font-medium text-gray-900 break-words">Furnished</p>
                                                        <p className="text-sm text-gray-500 break-words">Yes</p>
                                                    </div>
                                                </div>
                                            )}

                                            {property.details?.kitchen && (
                                                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                    <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                                                        <span className="text-emerald-600 text-xl">🍳</span>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-base font-medium text-gray-900 break-words">Kitchen</p>
                                                        <p className="text-sm text-gray-500 break-words">{property.details.kitchenType || 'Included'}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {property.details?.hasAC && (
                                                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                    <div className="w-12 h-12 rounded-full bg-cyan-50 flex items-center justify-center flex-shrink-0">
                                                        <span className="text-cyan-600 text-xl">❄️</span>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-base font-medium text-gray-900 break-words">Air Conditioning</p>
                                                        <p className="text-sm text-gray-500 break-words">Available</p>
                                                    </div>
                                                </div>
                                            )}

                                            {property.details?.hasSolar && (
                                                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                                    <div className="w-12 h-12 rounded-full bg-yellow-50 flex items-center justify-center flex-shrink-0">
                                                        <span className="text-yellow-600 text-xl">☀️</span>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-base font-medium text-gray-900 break-words">Solar Panels</p>
                                                        <p className="text-sm text-gray-500 break-words">Installed</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Description with Enhanced Design */}
                                {property.description && (
                                    <div className="bg-white rounded-2xl p-8 shadow-sm">
                                        <h3 className="text-2xl font-semibold text-gray-900 mb-6">About this property</h3>
                                        <div className="prose prose-lg max-w-none">
                                            <p className="text-gray-700 leading-relaxed">{property.description}</p>
                                        </div>
                                    </div>
                                )}

                                {/* Property Images Section */}
                                {hasValidImages && (
                                    <div className="bg-white rounded-2xl p-8 shadow-sm">
                                        <h3 className="text-2xl font-semibold text-gray-900 mb-6">Property Images</h3>
                                        {isLoadingImages ? (
                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                                {[...Array(4)].map((_, index) => (
                                                    <div key={index} className="aspect-[4/3] bg-gray-200 animate-pulse rounded-lg"></div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                                {allValidImages.slice(0, 8).map((imageUrl, index) => (
                                                    <div
                                                        key={index}
                                                        className="aspect-[4/3] overflow-hidden rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                                        onClick={() => setIsGalleryOpen(true)}
                                                    >
                                                        <img
                                                            src={imageUrl}
                                                            alt={`${property.title} - Image ${index + 1}`}
                                                            className="w-full h-full object-cover"
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                ))}
                                                {allValidImages.length > 8 && (
                                                    <div
                                                        className="aspect-[4/3] bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                                                        onClick={() => setIsGalleryOpen(true)}
                                                    >
                                                        <div className="text-center">
                                                            <div className="text-2xl mb-2">📷</div>
                                                            <div className="text-sm text-gray-600">+{allValidImages.length - 8} more</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Additional Information with Enhanced Design */}
                                <div className="bg-white rounded-2xl p-8 shadow-sm">
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-6">Additional Information</h3>
                                    <div className="space-y-6">
                                        {/* Company Name - Only for logged in users */}
                                        {userInfo && property.companyName && (
                                            <div className="border-b border-gray-100 pb-6">
                                                <h4 className="text-lg font-medium text-gray-900 mb-4">Partner</h4>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-2xl">🤝</span>
                                                    <p className="text-base text-gray-900">{property.companyName}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Location Details */}
                                        <div className="border-b border-gray-100 pb-6">
                                            <h4 className="text-lg font-medium text-gray-900 mb-4">Location Details</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-sm text-gray-500">City</p>
                                                    <p className="text-base text-gray-900">{property.address?.City || 'Not specified'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">State</p>
                                                    <p className="text-base text-gray-900">{property.address?.State || 'Not specified'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500">Country</p>
                                                    <p className="text-base text-gray-900">{property.address?.Country || 'Not specified'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Property Details */}
                                        <div className="border-b border-gray-100 pb-6">
                                            <h4 className="text-lg font-medium text-gray-900 mb-4">Property Details</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {property.details?.propertyType && (
                                                    <div>
                                                        <p className="text-sm text-gray-500">Property Type</p>
                                                        <p className="text-base text-gray-900">{property.details.propertyType}</p>
                                                    </div>
                                                )}
                                                {property.details?.constructionYear && (
                                                    <div>
                                                        <p className="text-sm text-gray-500">Construction Year</p>
                                                        <p className="text-base text-gray-900">{property.details.constructionYear}</p>
                                                    </div>
                                                )}
                                                {property.details?.heatingType && (
                                                    <div>
                                                        <p className="text-sm text-gray-500">Heating System</p>
                                                        <p className="text-base text-gray-900">{property.details.heatingType}</p>
                                                    </div>
                                                )}
                                                {property.details?.kitchen && (
                                                    <div>
                                                        <p className="text-sm text-gray-500">Kitchen</p>
                                                        <p className="text-base text-gray-900">{property.details.kitchenType || 'Included'}</p>
                                                    </div>
                                                )}
                                                {property.details?.sizeLiving > 0 && (
                                                    <div>
                                                        <p className="text-sm text-gray-500">Living Space</p>
                                                        <p className="text-base text-gray-900">{property.details.sizeLiving} m²</p>
                                                    </div>
                                                )}
                                                {property.details?.sizePlot > 0 && (
                                                    <div>
                                                        <p className="text-sm text-gray-500">Plot Size</p>
                                                        <p className="text-base text-gray-900">{property.details.sizePlot} m²</p>
                                                    </div>
                                                )}
                                                {property.details?.bedrooms > 0 && (
                                                    <div>
                                                        <p className="text-sm text-gray-500">Bedrooms</p>
                                                        <p className="text-base text-gray-900">{property.details.bedrooms}</p>
                                                    </div>
                                                )}
                                                {property.details?.bathrooms > 0 && (
                                                    <div>
                                                        <p className="text-sm text-gray-500">Bathrooms</p>
                                                        <p className="text-base text-gray-900">{property.details.bathrooms}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Amenities */}
                                        <div className="border-b border-gray-100 pb-6">
                                            <h4 className="text-lg font-medium text-gray-900 mb-4">Amenities</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {property.details?.amenities && property.details.amenities.length > 0 ? (
                                                    property.details.amenities.map((amenity, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                                            <p className="text-base text-gray-900">{amenity}</p>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className="text-gray-500">No amenities specified</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Additional Features */}
                                        {property.details?.features && property.details.features.length > 0 && (
                                            <div>
                                                <h4 className="text-lg font-medium text-gray-900 mb-4">Additional Features</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {property.details.features.map((feature, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                                            <p className="text-base text-gray-900">{feature}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Sidebar */}
                            <div className="hidden lg:block lg:col-span-4 space-y-6">
                                {/* Quick Summary Card with Enhanced Design */}
                                <div className="bg-white rounded-2xl p-8 shadow-sm sticky top-24">
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-8">Property Details</h3>

                                    {/* Key Features */}
                                    <div className="space-y-8">
                                        {/* Company Name - Only for logged in users */}
                                        {userInfo && property.companyName && (
                                            <div className="flex items-start gap-4">
                                                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-xl">🤝</span>
                                                </div>
                                                <div>
                                                    <p className="text-base font-medium text-gray-900">Partner</p>
                                                    <p className="text-sm text-gray-500 mt-2">{property.companyName}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Location */}
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                                                <span className="text-blue-600 text-xl">📍</span>
                                            </div>
                                            <div>
                                                <p className="text-base font-medium text-gray-900">Location</p>
                                                <p className="text-sm text-gray-500 mt-2">
                                                    {property.address?.StreetAddress && (
                                                        <>
                                                            {property.address.StreetAddress}
                                                            <br />
                                                        </>
                                                    )}
                                                    {property.address?.City && property.address?.State ? (
                                                        <>
                                                            {property.address.City}, {property.address.State}
                                                            <br />
                                                        </>
                                                    ) : (
                                                        <>
                                                            {property.address?.City || property.address?.State}
                                                            <br />
                                                        </>
                                                    )}
                                                    {property.address?.Country}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Property Type */}
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                                                <span className="text-purple-600 text-xl">🏠</span>
                                            </div>
                                            <div>
                                                <p className="text-base font-medium text-gray-900">Property Type</p>
                                                <p className="text-sm text-gray-500 mt-2">{property.details?.propertyType || 'Not specified'}</p>
                                            </div>
                                        </div>

                                        {/* Energy */}
                                        {property.details?.energyCertAvailable && (
                                            <div className="flex items-start gap-4">
                                                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-red-600 text-xl">⚡</span>
                                                </div>
                                                <div>
                                                    <p className="text-base font-medium text-gray-900">Energy</p>
                                                    <p className="text-sm text-gray-500 mt-2">Energy Certificate Available</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    {!isCompetitorPreviewOpened && !isPublic && (
                                        <div className="mt-8 pt-6 border-t">
                                            <PropertyActionButton
                                                property={property}
                                                additionalDetails={userInfo ? additionalDetails : null}
                                                onClose={onClose}
                                            />
                                        </div>
                                    )}

                                    {/* Contact Button for Public/Shareable Links */}
                                    {!isCompetitorPreviewOpened && isPublic && (
                                        <div className="mt-8 pt-6 border-t">
                                            <button
                                                onClick={() => setIsInquiryModalOpen(true)}
                                                className="w-full bg-my-blue hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                                            >
                                                <span>📧</span>
                                                <span>Contact About This Property</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <ModalLayout visible={visible} onClose={onClose} showCloseIcon={false} zIndex={zIndex}>
                <div className="hidden xl:block">
                    <Content />
                </div>
                <div className="block xl:hidden h-screen w-screen fixed bg-white overflow-auto top-0 left-0">
                    <Content />
                    {!isCompetitorPreviewOpened && !isPublic && (
                        <div className="fixed bottom-0 left-0 w-full bg-white border-t p-4">
                            <PropertyActionButton property={property} additionalDetails={userInfo ? additionalDetails : null} onClose={onClose} />
                        </div>
                    )}
                    {!isCompetitorPreviewOpened && isPublic && (
                        <div className="fixed bottom-0 left-0 w-full bg-white border-t p-4">
                            <button
                                onClick={() => setIsInquiryModalOpen(true)}
                                className="w-full bg-my-blue hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                            >
                                <span>📧</span>
                                <span>Contact About This Property</span>
                            </button>
                        </div>
                    )}
                </div>
            </ModalLayout>

            <ImageGalleryModal
                visible={isGalleryOpen}
                onClose={() => setIsGalleryOpen(false)}
                images={allValidImages}
                zIndex={(zIndex || DEFAULT_MODAL_Z_INDEX) + 1000}
            />

            <InquiryModal isOpen={isInquiryModalOpen} onClose={() => setIsInquiryModalOpen(false)} property={property} />
        </>
    );
};

export default MainPropertyDetailModal;
